import dayjs from 'dayjs';
import type {
  ChatbotActiveFilters,
  ChatbotFilterChipGroups,
  ChatbotFilterValue,
  ChatbotMessageFilter,
} from '@/models/chatbot';
import { FilterType } from '@/models/filter/FilterType';
import { FILTER_CONFIG } from '@/models/filter/filterConfig';
import { getDeepestHierarchyLevel, HIERARCHY_SEPARATOR } from '@/models/filter/utils/hierarchyPath';
import { Period, TimePeriod } from '@/types/date';
import type { SelectItem } from '@/types/ui/selectItem';
import { formatPeriodShortWithYear, formatPreviousDateRangeText } from '@/utils/date';
import { isTimePeriod } from '@/utils/date/isPeriod';
import { collapseTreeSelectValues } from '@/utils/filter/collapseTreeSelectValues';
import { isFilterType } from '@/utils/filter/isFilterType';

type TranslateFn = (key: string, options?: Record<string, string>) => string;

interface ChatbotDateRangeValue extends Record<string, ChatbotFilterValue> {
  beginn: string;
  end: string;
}

const DATE_RANGE_KEY = 'dateRange';
const COMPARISON_PERIOD_KEY = 'comparisonPeriod';
const COUNTRY_KEY = 'country';
const EMPTY_USER_COUNTRIES: SelectItem<string>[] = [];
const EMPTY_FILTER_CONTEXTS = { userCountries: EMPTY_USER_COUNTRIES };
const MAX_RATING_STARS = 5;
const SCIENTIFIC_RATING_LABEL_KEYS: Partial<Record<FilterType, string>> = {
  [FilterType.CONGRESS]: 'global.filters.congress',
  [FilterType.PUBLICATION]: 'global.filters.publication',
  [FilterType.CLINICAL_TRIAL]: 'global.filters.clinicalTrial',
  [FilterType.SOCIAL_MEDIA]: 'global.filters.socialMedia',
  [FilterType.GUIDELINE]: 'global.filters.guideline',
};
const HIERARCHICAL_FILTER_KEYS: ReadonlySet<string> = new Set([
  FilterType.COUNTRY,
  FilterType.HCO_COUNTRY,
  FilterType.TA_INDICATIONS,
  FilterType.INTERACTION_INDICATION,
  FilterType.INTERACTION_TYPE_ALL_CHANNELS,
  FilterType.SCIENTIFIC_ORGANIZATION,
]);

function isRecord(value: ChatbotFilterValue): value is Record<string, ChatbotFilterValue> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isDateRangeValue(value: ChatbotFilterValue | undefined): value is ChatbotDateRangeValue {
  return value !== undefined && isRecord(value) && typeof value.beginn === 'string' && typeof value.end === 'string';
}

function humanizeFilterKey(value: string): string {
  const spacedValue = value.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/_/g, ' ');

  return spacedValue.charAt(0).toUpperCase() + spacedValue.slice(1);
}

function flattenFilterValues(value: ChatbotFilterValue): string[] {
  if (value === null || typeof value === 'boolean') {
    return [];
  }

  if (typeof value === 'string' || typeof value === 'number') {
    return [String(value)];
  }

  if (Array.isArray(value)) {
    return value.flatMap(flattenFilterValues);
  }

  if (isDateRangeValue(value)) {
    return [];
  }

  if (isRecord(value)) {
    return Object.values(value).flatMap(flattenFilterValues);
  }

  return [];
}

function orderSelectedHierarchyGroups(values: string[]): string[] {
  const selectedValues = new Set(values);
  const groupRoots = new Set<string>();
  const emittedRoots = new Set<string>();

  for (const hierarchyValue of values) {
    const segments = hierarchyValue.split(HIERARCHY_SEPARATOR);

    for (let depth = 1; depth < segments.length; depth += 1) {
      const ancestor = segments.slice(0, depth).join(HIERARCHY_SEPARATOR);

      if (selectedValues.has(ancestor)) {
        groupRoots.add(ancestor);
        break;
      }
    }
  }

  return values.flatMap(hierarchyValue => {
    const segments = hierarchyValue.split(HIERARCHY_SEPARATOR);
    const root = segments
      .map((_, index) => segments.slice(0, index + 1).join(HIERARCHY_SEPARATOR))
      .find(path => groupRoots.has(path));

    if (!root) return [hierarchyValue];
    if (emittedRoots.has(root)) return [];

    emittedRoots.add(root);
    const descendants = values.filter(
      candidate => candidate !== root && candidate.startsWith(`${root}${HIERARCHY_SEPARATOR}`),
    );

    return [root, ...descendants];
  });
}

function formatScientificRating(filterKey: FilterType, value: string, t: TranslateFn): string | undefined {
  const labelKey = SCIENTIFIC_RATING_LABEL_KEYS[filterKey];
  const rating = Number(value);

  if (!labelKey || !Number.isFinite(rating)) return undefined;

  const activeStars = Math.max(0, Math.min(MAX_RATING_STARS, Math.floor(rating)));
  const stars = `${'\u2605'.repeat(activeStars)}${'\u2606'.repeat(MAX_RATING_STARS - activeStars)}`;

  return `${t(labelKey)} ${stars}`;
}

function getFilterLabelSegment(filterKey: string, t: TranslateFn): string {
  switch (filterKey) {
    case 'country':
      return t('components.aiChatDrawer.filterContext.labels.country');
    case 'dateRange':
      return t('components.aiChatDrawer.filterContext.labels.dateRange');
    case 'products':
      return t('components.aiChatDrawer.filterContext.labels.products');
    case 'taeLevels':
      return t('components.aiChatDrawer.filterContext.labels.taeLevels');
    case 'channelFocus':
      return t('components.aiChatDrawer.filterContext.labels.channelFocus');
    case 'aggregatedConsent':
      return t('components.aiChatDrawer.filterContext.labels.aggregatedConsent');
    case 'competitorAnalysis':
      return t('components.aiChatDrawer.filterContext.labels.competitorAnalysis');
    case 'globalBenchmarking':
      return t('components.aiChatDrawer.filterContext.labels.globalBenchmarking');
    case 'insights':
      return t('components.aiChatDrawer.filterContext.labels.insights');
    case 'partnerScore':
      return t('components.aiChatDrawer.filterContext.labels.partnerScore');
    case FilterType.SCIENTIFIC_ORGANIZATION:
      return t('global.filters.formControls.scientificOrganizationLabel');
    default:
      return humanizeFilterKey(filterKey);
  }
}

function getFilterLabel(filterPath: ReadonlyArray<string>, t: TranslateFn): string {
  return filterPath.map(filterKey => getFilterLabelSegment(filterKey, t)).join(' / ');
}

function formatFilterValue(filterPath: ReadonlyArray<string>, value: string, t: TranslateFn): string {
  const filterKey = filterPath.at(-1);

  if (filterKey && isFilterType(filterKey)) {
    const formattedRating = formatScientificRating(filterKey, value, t);
    if (formattedRating) return formattedRating;

    const formattedValue = FILTER_CONFIG[filterKey]?.format?.({ value, t, contexts: EMPTY_FILTER_CONTEXTS });
    if (typeof formattedValue === 'string') return formattedValue;
  }

  return filterKey && HIERARCHICAL_FILTER_KEYS.has(filterKey) ? getDeepestHierarchyLevel(value) : value;
}

function getDisplayValues(
  filterPath: ReadonlyArray<string>,
  value: ChatbotFilterValue,
  collapseHierarchicalValues: boolean,
): string[] {
  const values = flattenFilterValues(value);
  const filterKey = filterPath.at(-1);
  const filterConfig = filterKey && isFilterType(filterKey) ? FILTER_CONFIG[filterKey] : undefined;

  if (!collapseHierarchicalValues || filterConfig?.selectionMode !== 'indication') {
    return filterKey && HIERARCHICAL_FILTER_KEYS.has(filterKey) ? orderSelectedHierarchyGroups(values) : values;
  }

  return collapseTreeSelectValues(values).map(collapsedValue => collapsedValue.label);
}

function toPeriod(value: ChatbotDateRangeValue): Period | undefined {
  const beginn = dayjs(value.beginn);
  const end = dayjs(value.end);

  if (!beginn.isValid() || !end.isValid()) {
    return undefined;
  }

  const period = isTimePeriod(value.period) ? value.period : TimePeriod.CUSTOM;

  return { beginn, end, period };
}

function buildDateRangeChip(
  dateRangeValue: ChatbotDateRangeValue,
  comparisonValue: ChatbotFilterValue | undefined,
  t: TranslateFn,
): ChatbotMessageFilter | undefined {
  const period = toPeriod(dateRangeValue);

  if (!period) {
    return undefined;
  }

  const dateRangeText = formatPeriodShortWithYear(period);
  const comparisonPeriod = isDateRangeValue(comparisonValue) ? toPeriod(comparisonValue) : undefined;
  const previousSource = comparisonPeriod ?? (period.period === TimePeriod.CUSTOM ? undefined : period);
  const previousText = previousSource ? formatPreviousDateRangeText(previousSource, t) : null;

  return {
    label: getFilterLabel([DATE_RANGE_KEY], t),
    value: previousText ? `${dateRangeText} ${previousText}` : dateRangeText,
  };
}

function buildValueChips(
  filterPath: ReadonlyArray<string>,
  value: ChatbotFilterValue,
  t: TranslateFn,
  collapseHierarchicalValues = false,
): ChatbotMessageFilter[] {
  if (!isRecord(value) || isDateRangeValue(value)) {
    const label = getFilterLabel(filterPath, t);
    const chipsByValue = new Map<string, ChatbotMessageFilter>();

    getDisplayValues(filterPath, value, collapseHierarchicalValues).forEach(filterValue => {
      const formattedValue = formatFilterValue(filterPath, filterValue, t);

      if (!chipsByValue.has(formattedValue)) {
        chipsByValue.set(formattedValue, { label, value: formattedValue });
      }
    });

    return [...chipsByValue.values()];
  }

  return Object.entries(value).flatMap(([nestedKey, nestedValue]) =>
    buildValueChips([...filterPath, nestedKey], nestedValue, t, collapseHierarchicalValues),
  );
}

function buildResolvedCountryChips(
  resolvedCountryCodes: ReadonlyArray<string>,
  t: TranslateFn,
): ChatbotMessageFilter[] {
  const label = t('components.aiChatDrawer.filterContext.labels.country');

  return resolvedCountryCodes.map(countryCode => ({ label, value: countryCode }));
}

export function buildChatbotFilterChips(
  activeFilters: ChatbotActiveFilters,
  t: TranslateFn,
): ReadonlyArray<ChatbotMessageFilter> {
  const {
    [DATE_RANGE_KEY]: dateRangeValue,
    [COMPARISON_PERIOD_KEY]: comparisonValue,
    [COUNTRY_KEY]: countryValue,
    ...remainingGlobalFilters
  } = activeFilters.globalFilters ?? {};

  const dateRangeChip = isDateRangeValue(dateRangeValue)
    ? buildDateRangeChip(dateRangeValue, comparisonValue, t)
    : undefined;

  const countryChips =
    countryValue === undefined
      ? buildResolvedCountryChips(activeFilters.resolvedCountryCodes ?? [], t)
      : buildValueChips([COUNTRY_KEY], countryValue, t);

  const remainingGlobalChips = Object.entries(remainingGlobalFilters).flatMap(([filterKey, value]) =>
    buildValueChips([filterKey], value, t),
  );

  return [...(dateRangeChip ? [dateRangeChip] : []), ...countryChips, ...remainingGlobalChips];
}

export function buildChatbotLocalFilterChips(
  activeFilters: ChatbotActiveFilters,
  t: TranslateFn,
): ReadonlyArray<ChatbotMessageFilter> {
  return Object.entries(activeFilters.localFilters ?? {}).flatMap(([filterKey, value]) =>
    buildValueChips([filterKey], value, t, true),
  );
}

export function buildChatbotFilterChipGroups(
  activeFilters: ChatbotActiveFilters,
  t: TranslateFn,
): ChatbotFilterChipGroups {
  return {
    globalFilters: buildChatbotFilterChips(activeFilters, t),
    localFilters: buildChatbotLocalFilterChips(activeFilters, t),
  };
}
